import type { GqlClient } from '@/types/gqlClient'
import { GET_FILE_DOWNLOAD_URL_MUTATION, FileDownloadUrlResponse, GetFileDownloadUrlVariables } from '../graphql/download'

export type GqlRequestClient = {
  request<T = Record<string, unknown>>(
    query: string | unknown,
    variables?: Record<string, unknown>
  ): Promise<T>
}

export interface ProjectFile {
  id: string
  name: string
  status: 'PROCESSING' | 'COMPLETE' | 'ERROR'
  size?: number
  mimeType?: string
  usage?: string
  section?: string
  uploadedBy?: number
  createdAt: string
  updatedAt: string
  enabled: boolean
  thumbnail?: boolean
  projectId: number
}

export interface ProjectFileMetadata {
  projectId: number
  fileId: string
  usage?: string
  section?: string
  status?: 'ACCEPTED' | 'REJECTED'
  accountVisibility?: string
  comment?: string
}

const GET_PROJECT_FILES_QUERY = `
  query GetProjectFiles($projectId: Int!) {
    project_files(
      where: {
        project_id: { _eq: $projectId }
        file: { enabled: { _eq: true } }
      }
      order_by: { file: { created_at: desc } }
    ) {
      project_id
      file_id
      use
      section
      status
      account_visibility
      comment
      file {
        id
        name
        status
        size
        mime_type
        uploaded_by
        created_at
        updated_at
        enabled
        thumbnail
      }
    }
  }
`

const DELETE_PROJECT_FILE_MUTATION = `
  mutation DeleteProjectFile($fileId: uuid!) {
    update_files(
      where: { id: { _eq: $fileId } }
      _set: { enabled: false }
    ) {
      affected_rows
    }
  }
`

const UPDATE_PROJECT_FILE_METADATA_MUTATION = `
  mutation UpdateProjectFileMetadata(
    $projectId: Int!
    $fileId: uuid!
    $use: String
    $section: String
    $status: String
    $comment: String
    $accountVisibility: String
  ) {
    insert_project_files(
      objects: {
        project_id: $projectId
        file_id: $fileId
        use: $use
        section: $section
        status: $status
        comment: $comment
        account_visibility: $accountVisibility
      }
      on_conflict: {
        constraint: project_files_pkey
        update_columns: [use, section, status, comment, account_visibility]
      }
    ) {
      affected_rows
      returning {
        project_id
        file_id
        use
        section
        status
        comment
        account_visibility
      }
    }
  }
`

/**
 * Service to manage project files and their metadata
 */
export class ProjectFileService {
  constructor(private graphqlClient: GqlRequestClient) {}

  /**
   * Gets all files for a project
   */
  async getProjectFiles(projectId: number): Promise<ProjectFile[]> {
    try {
      const response = await this.graphqlClient.request<{
        project_files: Array<{
          project_id: number
          file_id: string
          use?: string
          section?: string
          status?: string
          account_visibility?: string
          comment?: string
          file: {
            id: string
            name: string
            status: 'PROCESSING' | 'COMPLETE' | 'ERROR'
            size?: number
            mime_type?: string
            uploaded_by?: number
            created_at: string
            updated_at: string
            enabled: boolean
            thumbnail?: boolean
          }
        }>
      }>(GET_PROJECT_FILES_QUERY, { projectId })

      return response.project_files.map((projectFile) => ({
        id: projectFile.file.id,
        name: projectFile.file.name,
        status: projectFile.file.status as 'PROCESSING' | 'COMPLETE' | 'ERROR',
        size: projectFile.file.size,
        mimeType: projectFile.file.mime_type,
        usage: projectFile.use,
        section: projectFile.section,
        uploadedBy: projectFile.file.uploaded_by,
        createdAt: projectFile.file.created_at,
        updatedAt: projectFile.file.updated_at,
        enabled: projectFile.file.enabled,
        thumbnail: projectFile.file.thumbnail,
        projectId: projectFile.project_id,
      }))
    } catch (error) {
      console.error('Error fetching project files:', error)
      throw new Error('Failed to fetch project files')
    }
  }

  /**
   * Gets files for a project filtered by usage type
   */
  async getProjectFilesByUsage(projectId: number, usageTypes: string[]): Promise<ProjectFile[]> {
    const allFiles = await this.getProjectFiles(projectId)
    return allFiles.filter(file => file.usage && usageTypes.includes(file.usage))
  }

  /**
   * Gets files for a project grouped by usage type
   */
  async getProjectFilesGroupedByUsage<T extends string>(
    projectId: number,
    usageMap: Record<T, string>
  ): Promise<Record<T, ProjectFile[]>> {
    const allFiles = await this.getProjectFiles(projectId)
    const grouped: Record<T, ProjectFile[]> = {} as Record<T, ProjectFile[]>

    // Initialize all groups
    Object.keys(usageMap).forEach(key => {
      grouped[key as T] = []
    })

    // Group files by usage type
    allFiles.forEach(file => {
      if (file.usage) {
        const groupKey = this.getGroupKeyFromUsage(file.usage, usageMap)
        if (groupKey) {
          grouped[groupKey].push(file)
        }
      }
    })

    return grouped
  }

  /**
   * Deletes a file (soft delete by setting enabled = false)
   */
  async deleteFile(fileId: string): Promise<boolean> {
    try {
      const response = await this.graphqlClient.request<{
        update_files: { affected_rows: number }
      }>(DELETE_PROJECT_FILE_MUTATION, { fileId })

      return response.update_files.affected_rows > 0
    } catch (error) {
      console.error('Error deleting file:', error)
      throw new Error('Failed to delete file')
    }
  }

  /**
   * Updates file metadata for a project
   */
  async updateFileMetadata(metadata: ProjectFileMetadata): Promise<boolean> {
    try {
      const response = await this.graphqlClient.request<{
        insert_project_files: { affected_rows: number }
      }>(UPDATE_PROJECT_FILE_METADATA_MUTATION, {
        projectId: metadata.projectId,
        fileId: metadata.fileId,
        use: metadata.usage,
        section: metadata.section,
        status: metadata.status,
        comment: metadata.comment,
        accountVisibility: metadata.accountVisibility || 'ARCHITECT',
      })

      return response.insert_project_files.affected_rows > 0
    } catch (error) {
      console.error('Error updating file metadata:', error)
      throw new Error('Failed to update file metadata')
    }
  }

  /**
   * Gets the download URL for a file via Hasura action
   */
  async getFileDownloadUrl(fileId: string, derivative?: 'original' | 'thumbnail' | 'pdf-optimized' | 'watermarked'): Promise<FileDownloadUrlResponse> {
    const variables: GetFileDownloadUrlVariables = {
      fileId,
      derivative: derivative || 'original'
    }

    const response = await this.graphqlClient.request<{ getFileDownloadUrl: FileDownloadUrlResponse }>(
      GET_FILE_DOWNLOAD_URL_MUTATION,
      variables
    )

    return response.getFileDownloadUrl
  }

  /**
   * Helper method to map usage type to group key
   */
  private getGroupKeyFromUsage<T extends string>(usage: string, usageMap: Record<T, string>): T | null {
    for (const [groupKey, usageType] of Object.entries(usageMap)) {
      if (usageType === usage) {
        return groupKey as T
      }
    }
    return null
  }
}

// Standalone service functions for use in hooks (following project pattern)

/**
 * Gets all files for a project
 */
export const getProjectFiles = (gqlClient: GqlClient, projectId: number): Promise<ProjectFile[]> => {
  const service = new ProjectFileService(gqlClient as unknown as GqlRequestClient)
  return service.getProjectFiles(projectId)
}

/**
 * Deletes a project file
 */
export const deleteProjectFile = (gqlClient: GqlClient, fileId: string): Promise<boolean> => {
  const service = new ProjectFileService(gqlClient as unknown as GqlRequestClient)
  return service.deleteFile(fileId)
}

/**
 * Gets download URL for a file via Hasura action
 */
export const getFileDownloadUrl = async (
  gqlClient: GqlClient,
  fileId: string,
  derivative?: 'original' | 'thumbnail' | 'pdf-optimized' | 'watermarked'
): Promise<FileDownloadUrlResponse> => {
  const service = new ProjectFileService(gqlClient as unknown as GqlRequestClient)
  return service.getFileDownloadUrl(fileId, derivative)
}

/**
 * Gets project files grouped by usage type
 */
export const getProjectFilesGroupedByUsage = <T extends string>(
  gqlClient: GqlClient,
  projectId: number,
  usageMap: Record<T, string>
): Promise<Record<T, ProjectFile[]>> => {
  const service = new ProjectFileService(gqlClient as unknown as GqlRequestClient)
  return service.getProjectFilesGroupedByUsage(projectId, usageMap)
}
